import React, { useEffect, useRef, useState } from 'react';
import { translate } from '../../shared/kit/hooks/useGasI18n';
import type { QuizApi } from './api';
import type { QuizCueData, QuizQuestion, QuizAnswer, QuizCheckResult } from './types';

interface QuizCueOverlayProps {
    cueData: QuizCueData;
    api: QuizApi;
    onAnswered: (quizId: number, answer: QuizAnswer, result: QuizCheckResult | null) => void;
    onClose: () => void;
}

type OverlayState =
    | { phase: 'loading' }
    | { phase: 'error' }
    | { phase: 'question'; quiz_id: number; question: QuizQuestion }
    | { phase: 'result'; quiz_id: number; question: QuizQuestion; answer: QuizAnswer; result: QuizCheckResult | null };

export function QuizCueOverlay({ cueData, api, onAnswered, onClose }: QuizCueOverlayProps) {
    const [state, setState] = useState<OverlayState>({ phase: 'loading' });
    // Sélection courante pour les questions à choix
    const [selectedIndex, setSelectedIndex] = useState<number | null>(null);
    const [selectedIndices, setSelectedIndices] = useState<number[]>([]);
    const [selectedBool, setSelectedBool] = useState<boolean | null>(null);
    const startedAtRef = useRef(Date.now());

    useEffect(() => {
        let cancelled = false;
        setState({ phase: 'loading' });
        setSelectedIndex(null);
        setSelectedIndices([]);
        setSelectedBool(null);
        startedAtRef.current = Date.now();

        api.loadQuestionsForGroup(cueData.quiz_group_id).then((resp) => {
            if (cancelled) return;
            if (!resp) {
                setState({ phase: 'error' });
                return;
            }
            const question = resp.questions.find((q) => q.id === cueData.question_id) ?? null;
            if (!question) {
                setState({ phase: 'error' });
                return;
            }
            setState({ phase: 'question', quiz_id: resp.quiz_id, question });
        });

        return () => { cancelled = true; };
    }, [cueData.quiz_group_id, cueData.question_id, api]);

    function buildAnswer(question: QuizQuestion): QuizAnswer | null {
        const timeSpent = Math.round((Date.now() - startedAtRef.current) / 1000);
        if (question.type === 'qcm') {
            if (question.multiple_answers) {
                if (selectedIndices.length === 0) return null;
                return { question_id: question.id, selected_indices: selectedIndices, time_spent: timeSpent };
            }
            if (selectedIndex === null) return null;
            return { question_id: question.id, selected_index: selectedIndex, time_spent: timeSpent };
        }
        if (question.type === 'vrai_faux') {
            if (selectedBool === null) return null;
            return { question_id: question.id, selected_answer: selectedBool, time_spent: timeSpent };
        }
        // Types non interactifs immédiatement (open, ordering, etc.) → réponse vide, pas de check-answer
        return null;
    }

    async function handleCheck() {
        if (state.phase !== 'question') return;
        const { quiz_id, question } = state;
        const answer = buildAnswer(question);
        if (!answer) return;

        // check-answer pour les types automatiquement notés ; les autres passent directement
        const canCheck = question.type === 'qcm' || question.type === 'vrai_faux';
        const result = canCheck ? await api.checkAnswer(quiz_id, question.id, answer, '') : null;
        setState({ phase: 'result', quiz_id, question, answer, result });
    }

    function handleContinue() {
        if (state.phase !== 'result') {
            onClose();
            return;
        }
        onAnswered(state.quiz_id, state.answer, state.result);
        onClose();
    }

    return (
        <div className="gas-video-quiz-overlay" role="dialog" aria-modal="true" aria-label={translate('quiz.title')}>
            <div className="gas-video-quiz-overlay__panel">
                {state.phase === 'loading' ? (
                    <p className="gas-video-quiz-overlay__loading">{translate('video.player.quiz.loading')}</p>
                ) : state.phase === 'error' ? (
                    <>
                        <p className="gas-video-quiz-overlay__error">{translate('video.player.quiz.error')}</p>
                        <button type="button" className="gas-button gas-button--secondary" onClick={onClose}>
                            {translate('common.continue')}
                        </button>
                    </>
                ) : state.phase === 'question' ? (
                    <QuestionForm
                        question={state.question}
                        selectedIndex={selectedIndex}
                        selectedIndices={selectedIndices}
                        selectedBool={selectedBool}
                        onSelectIndex={setSelectedIndex}
                        onToggleIndex={(index) => setSelectedIndices((current) => current.includes(index)
                            ? current.filter((value) => value !== index)
                            : [...current, index].sort((a, b) => a - b))}
                        onSelectBool={setSelectedBool}
                        onCheck={handleCheck}
                        onSkip={onClose}
                    />
                ) : (
                    <ResultView result={state.result} answer={state.answer} onContinue={handleContinue} />
                )}
            </div>
        </div>
    );
}

// ─── Question form ───────────────────────────────────────────────────────────

interface QuestionFormProps {
    question: QuizQuestion;
    selectedIndex: number | null;
    selectedIndices: number[];
    selectedBool: boolean | null;
    onSelectIndex: (i: number) => void;
    onToggleIndex: (i: number) => void;
    onSelectBool: (v: boolean) => void;
    onCheck: () => void;
    onSkip: () => void;
}

function QuestionForm({ question, selectedIndex, selectedIndices, selectedBool, onSelectIndex, onToggleIndex, onSelectBool, onCheck, onSkip }: QuestionFormProps) {
    const canSubmit = question.type === 'qcm'
        ? question.multiple_answers ? selectedIndices.length > 0 : selectedIndex !== null
        : question.type === 'vrai_faux'
            ? selectedBool !== null
            : false;

    const isAutoCheckable = question.type === 'qcm' || question.type === 'vrai_faux';

    return (
        <>
            <p className="gas-video-quiz-overlay__question">{question.question}</p>
            {question.image_url ? <img className="gas-video-quiz-overlay__question-image" src={question.image_url} alt={question.image_alt || ''} /> : null}
            {question.multiple_answers ? <p>{translate('quiz.player.multiple_answers_hint')}</p> : null}

            {question.type === 'qcm' && question.choices ? (
                <ul className="gas-video-quiz-overlay__choices">
                    {question.choices.map((choice, i) => (
                        <li key={i}>
                            <label className={'gas-video-quiz-overlay__choice' + ((question.multiple_answers ? selectedIndices.includes(i) : selectedIndex === i) ? ' gas-video-quiz-overlay__choice--selected' : '')}>
                                <input
                                    type={question.multiple_answers ? 'checkbox' : 'radio'}
                                    name="quiz-choice"
                                    checked={question.multiple_answers ? selectedIndices.includes(i) : selectedIndex === i}
                                    onChange={() => question.multiple_answers ? onToggleIndex(i) : onSelectIndex(i)}
                                    className="gas-video-quiz-overlay__radio"
                                />
                                {choice}
                            </label>
                        </li>
                    ))}
                </ul>
            ) : question.type === 'vrai_faux' ? (
                <div className="gas-video-quiz-overlay__bool-choices">
                    <label className={'gas-video-quiz-overlay__choice' + (selectedBool === true ? ' gas-video-quiz-overlay__choice--selected' : '')}>
                        <input type="radio" name="quiz-bool" checked={selectedBool === true} onChange={() => onSelectBool(true)} className="gas-video-quiz-overlay__radio" />
                        Vrai
                    </label>
                    <label className={'gas-video-quiz-overlay__choice' + (selectedBool === false ? ' gas-video-quiz-overlay__choice--selected' : '')}>
                        <input type="radio" name="quiz-bool" checked={selectedBool === false} onChange={() => onSelectBool(false)} className="gas-video-quiz-overlay__radio" />
                        Faux
                    </label>
                </div>
            ) : (
                <p className="gas-video-quiz-overlay__manual-note">{translate('quiz.labels.review_pending')}</p>
            )}

            <div className="gas-video-quiz-overlay__actions">
                {isAutoCheckable ? (
                    <button
                        type="button"
                        className="gas-button gas-button--primary"
                        disabled={!canSubmit}
                        onClick={onCheck}
                    >
                        {translate('video.player.quiz.check')}
                    </button>
                ) : null}
                <button type="button" className="gas-button gas-button--outline" onClick={onSkip}>
                    {translate('common.continue')}
                </button>
            </div>
        </>
    );
}

// ─── Result view ─────────────────────────────────────────────────────────────

interface ResultViewProps {
    result: QuizCheckResult | null;
    answer: QuizAnswer;
    onContinue: () => void;
}

function ResultView({ result, onContinue }: ResultViewProps) {
    const label = result === null
        ? translate('quiz.labels.review_pending')
        : result.correct
            ? translate('video.player.quiz.correct')
            : translate('video.player.quiz.incorrect');

    const mod = result === null ? '' : result.correct ? ' gas-video-quiz-overlay__result--correct' : ' gas-video-quiz-overlay__result--incorrect';

    return (
        <>
            <p className={'gas-video-quiz-overlay__result' + mod}>{label}</p>
            {result?.explanation ? (
                <p className="gas-video-quiz-overlay__explanation">{result.explanation}</p>
            ) : null}
            <div className="gas-video-quiz-overlay__actions">
                <button type="button" className="gas-button gas-button--primary" onClick={onContinue}>
                    {translate('common.continue')}
                </button>
            </div>
        </>
    );
}
